import React, { useMemo, useRef, useState } from 'react';
import { Box, IconButton, Menu, MenuItem } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import { Color } from '@tiptap/extension-color';
import { InlineTextStyle, LiveTextEditor, clearTextSelection, rememberTextSelection } from './textInlineMarks';
import { SlideObject, VisibleAsset, resolveSlideFontFamily, resolvedFontWeight, resolvedLineHeight, slideSize } from '../types';
import { PRES } from '../chrome';
import { PRESENTATION_ASSET_DRAG } from './AssetBrowser';
import { hasLibraryFieldTransfer, LibraryFieldTransfer, readLibraryFieldTransfer } from './libraryFieldTransfer';
import { readClipboardImageFile } from './clipboardImage';
import { mediaRadiusSx, objectPreviewHtml, objectSurfaceSx, resolveMediaUrl, textFlowSx } from './slidePreview';
import SelectionChrome, { CropSessionBar, ImageScaleHandles, ScreenAlignedCropHandles, ScreenAlignedResizeHandles } from './SelectionChrome';
import { hasEditableText, isCroppableObject, isFillableShape, isLineShape, objectHoverCursor } from './shapeFrame';
import { caretPosFromPoint } from './textCaret';
import { presentationEditorHtml, sanitizePresentationHtml } from './textSanitize';
import { objectEffectSx } from './objectEffects';
import ShapeObjectView from './shapeObjectView';
import ChartObjectView from './ChartObjectView';
import TableObjectView from './TableObjectView';
import { normalizeTable } from './tableModel';
import {
  imageCropStyle,
  ResizeHandle,
  rotationFromPointer,
} from './objectTransform';
import { canGroup, canMoveSelection, canUngroup, cloneSelection, gestureTargetIds, selectionBounds, selectionIsolateMode, shouldEnterTextEditOnSelect } from './objectGroup';
import { applyTextBoxFit } from './textBoxFit';
import { fitImageInFrame } from './placeTarget';
import { canStartDrag, createDragController, DragPreview, MOVE_THRESHOLD_PX } from './dragSession';
import { SmartGuideLine } from './smartGuides';
import { applyViewportPan, canStartSpacePan, isSpacePanKey } from './canvasPan';
import { consumeFlag, shouldApplyPendingAdditiveToggle, shouldDeferAdditiveToggle, shouldKeepTextEdit } from './selectionGesture';
import { formatZoomPercent, isZoomKey, steppedZoom } from './canvasZoom';
import {
  isMarqueeGesture,
  nextMarqueeSelection,
  normalizeRect,
  objectsIntersectingRect,
  slidePointFromClient,
  type MarqueeRect,
} from './marqueeSelection';
import { COPY } from '../presentationCopy';
import { resolveBoundField } from './linkedFields';
import { hasTextContentChanged } from './boundTextEdit';

interface SlideCanvasProps {
  objects: SlideObject[];
  selectedObjectId: string | null;
  selectedObjectIds?: string[];
  advancedMode: boolean;
  showGrid: boolean;
  snap: boolean;
  smartGuides?: boolean;
  isolatedObjectId?: string | null;
  onSelect: (objectId: string | null, options?: { additive?: boolean; isolate?: boolean; ids?: string[] }) => void;
  onPrepareTextEdit?: (objectId: string) => void;
  onBeforeTextContentChange?: (object: SlideObject, html: string) => boolean;
  onMakeBoundFieldLocal?: (objectId: string) => void;
  onBringToFront?: () => void;
  onSendToBack?: () => void;
  onShowInLibrary?: (assetId: string) => void;
  onGroup?: () => void;
  onUngroup?: () => void;
  onChange: (objects: SlideObject[]) => void;
  onOpenAssets: (objectId: string) => void;
  onDropAsset?: (asset: VisibleAsset, x: number, y: number) => void;
  onDropText?: (payload: LibraryFieldTransfer, x: number, y: number) => void;
  onDropFiles?: (file: File | null, x: number, y: number) => void;
  backgroundColor?: string;
  croppingId?: string | null;
  onCroppingChange?: (id: string | null) => void;
  editingTextId?: string | null;
  onEditingTextChange?: (id: string | null) => void;
  onTextEditorReady?: (live: LiveTextEditor, mounted: boolean) => void;
  onHistoryBegin?: () => void;
  onGestureCommit?: (objects: SlideObject[]) => void;
  onTextBoxFit?: (objectId: string, html: string) => void;
  onTextStyle?: (patch: Record<string, unknown>) => void;
  onToggleMark?: (mark: 'bold' | 'italic' | 'underline') => void;
  queryTextMark?: (mark: 'bold' | 'italic' | 'underline') => boolean | null;
  showAi?: boolean;
  aiBusy?: boolean;
  aiBusyLabel?: string;
  onAiRewrite?: () => void;
  onAiGrammar?: () => void;
  onAiTranslate?: () => void;
  readOnly?: boolean;
  pageFormat?: string | null;
}

function stripHtml(value: string): string {
  return String(value || '').replace(/<[^>]+>/g, '');
}

const TextObjectEditor: React.FC<{
  object: SlideObject;
  locked: boolean;
  focusPoint?: { x: number; y: number } | null;
  onCommit: (html: string) => void;
  onReady?: (live: LiveTextEditor, mounted: boolean) => void;
  onFit?: (html: string) => void;
  onBeforeContentChange?: (html: string) => boolean;
}> = ({ object, locked, focusPoint, onCommit, onReady, onFit, onBeforeContentChange }) => {
  const dirtyRef = React.useRef(false);
  const readyRef = React.useRef(false);
  const onReadyRef = React.useRef(onReady);
  const onCommitRef = React.useRef(onCommit);
  const onFitRef = React.useRef(onFit);
  const onBeforeContentChangeRef = React.useRef(onBeforeContentChange);
  const fitFrameRef = React.useRef(0);
  const focusPointRef = React.useRef(focusPoint);
  onReadyRef.current = onReady;
  onCommitRef.current = onCommit;
  onFitRef.current = onFit;
  onBeforeContentChangeRef.current = onBeforeContentChange;
  focusPointRef.current = focusPoint;
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: false }),
      Underline,
      InlineTextStyle,
      Color,
      Placeholder.configure({ placeholder: 'Skriv text…' }),
    ],
    content: presentationEditorHtml(object.content) || '',
    editable: !locked,
    autofocus: false,
    onSelectionUpdate: ({ editor: instance }) => rememberTextSelection(instance),
    onTransaction: ({ editor: instance }) => rememberTextSelection(instance),
    onUpdate: ({ editor: instance, transaction }) => {
      if (readyRef.current && transaction.docChanged) {
        const html = instance.getHTML();
        if (
          hasTextContentChanged(object.content, html)
          && onBeforeContentChangeRef.current
          && !onBeforeContentChangeRef.current(html)
        ) {
          readyRef.current = false;
          dirtyRef.current = false;
          instance.commands.setContent(presentationEditorHtml(object.content) || '', false);
          readyRef.current = true;
          return;
        }
        dirtyRef.current = true;
        if (onFitRef.current) {
          if (fitFrameRef.current) window.cancelAnimationFrame(fitFrameRef.current);
          fitFrameRef.current = window.requestAnimationFrame(() => {
            fitFrameRef.current = 0;
            onFitRef.current?.(html);
          });
        }
      }
    },
    onBlur: ({ editor: instance }) => {
      rememberTextSelection(instance);
      if (dirtyRef.current) onCommit(instance.getHTML());
    },
  }, [object.id]);

  React.useEffect(() => {
    if (!editor) return;
    readyRef.current = false;
    onReadyRef.current?.({
      objectId: object.id,
      editor,
      markClean: () => { dirtyRef.current = false; },
    }, true);
    let frame2 = 0;
    const frame1 = window.requestAnimationFrame(() => {
      frame2 = window.requestAnimationFrame(() => {
        readyRef.current = true;
        if (locked || editor.isDestroyed) return;
        const point = focusPointRef.current;
        if (point) {
          const pos = caretPosFromPoint(editor.view, point.x, point.y);
          if (pos != null) {
            editor.chain().focus().setTextSelection(pos).run();
            return;
          }
        }
        editor.commands.focus();
      });
    });
    return () => {
      window.cancelAnimationFrame(frame1);
      window.cancelAnimationFrame(frame2);
      if (fitFrameRef.current) window.cancelAnimationFrame(fitFrameRef.current);
      if (dirtyRef.current && !editor.isDestroyed) {
        onCommitRef.current(editor.getHTML());
        dirtyRef.current = false;
      }
      clearTextSelection(editor);
      onReadyRef.current?.({ objectId: object.id, editor }, false);
    };
  }, [editor, object.id]);

  return (
    <EditorContent
      editor={editor}
      style={{ width: '100%', height: '100%', flex: 1, minHeight: 0, outline: 'none', cursor: 'text', userSelect: 'text' }}
    />
  );
};

const SmartGuidesOverlay: React.FC<{
  guides?: SmartGuideLine[];
  pageSize: { width: number; height: number };
  scale: number;
}> = ({ guides, pageSize, scale }) => {
  if (!guides?.length) return null;
  const stroke = Math.max(1 / scale, 0.75);
  return (
    <>
      {guides.map((guide, index) => {
        if (guide.kind === 'align') {
          return (
            <Box
              key={`align-${guide.axis}-${guide.position}-${index}`}
              sx={{
                position: 'absolute',
                pointerEvents: 'none',
                zIndex: 20000,
                left: guide.axis === 'x' ? guide.position : 0,
                top: guide.axis === 'y' ? guide.position : 0,
                width: guide.axis === 'x' ? 0 : pageSize.width,
                height: guide.axis === 'y' ? 0 : pageSize.height,
                borderLeft: guide.axis === 'x' ? `${stroke}px dashed ${PRES.guide}` : undefined,
                borderTop: guide.axis === 'y' ? `${stroke}px dashed ${PRES.guide}` : undefined,
              }}
            />
          );
        }
        if (guide.start == null || guide.end == null || guide.cross == null) return null;
        const length = guide.end - guide.start;
        if (length <= 0) return null;
        const tick = 8;
        if (guide.axis === 'x') {
          return (
            <Box
              key={`gap-${index}`}
              sx={{ position: 'absolute', pointerEvents: 'none', zIndex: 20000, left: guide.start, top: guide.cross - tick / 2, width: length, height: tick }}
            >
              <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, borderLeft: `${stroke}px solid ${PRES.guide}` }} />
              <Box sx={{ position: 'absolute', left: 0, right: 0, top: '50%', borderTop: `${stroke}px dashed ${PRES.guide}` }} />
              <Box sx={{ position: 'absolute', right: 0, top: 0, bottom: 0, borderRight: `${stroke}px solid ${PRES.guide}` }} />
            </Box>
          );
        }
        return (
          <Box
            key={`gap-${index}`}
            sx={{ position: 'absolute', pointerEvents: 'none', zIndex: 20000, top: guide.start, left: guide.cross - tick / 2, height: length, width: tick }}
          >
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, borderTop: `${stroke}px solid ${PRES.guide}` }} />
            <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: '50%', borderLeft: `${stroke}px dashed ${PRES.guide}` }} />
            <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, borderBottom: `${stroke}px solid ${PRES.guide}` }} />
          </Box>
        );
      })}
    </>
  );
};

const SlideCanvas: React.FC<SlideCanvasProps> = ({
  objects,
  selectedObjectId,
  selectedObjectIds: selectedObjectIdsProp,
  isolatedObjectId = null,
  onPrepareTextEdit,
  onBeforeTextContentChange,
  onMakeBoundFieldLocal,
  advancedMode,
  showGrid,
  snap,
  smartGuides = true,
  onSelect,
  onBringToFront,
  onSendToBack,
  onShowInLibrary,
  onGroup,
  onUngroup,
  onChange,
  onOpenAssets,
  onDropAsset,
  onDropText,
  onDropFiles,
  backgroundColor = '#FFFFFF',
  croppingId = null,
  onCroppingChange,
  editingTextId = null,
  onEditingTextChange,
  onTextEditorReady,
  onHistoryBegin,
  onGestureCommit,
  onTextBoxFit,
  onTextStyle,
  onToggleMark,
  queryTextMark,
  showAi,
  aiBusy,
  aiBusyLabel,
  onAiRewrite,
  onAiGrammar,
  onAiTranslate,
  readOnly,
  pageFormat,
}) => {
  const size = slideSize(pageFormat);
  const frameRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const dropConsumedRef = useRef(false);
  const [fitScale, setFitScale] = useState(0.42);
  const [zoomMode, setZoomMode] = useState<'fit' | number>('fit');
  const [dragPreview, setDragPreview] = useState<DragPreview | null>(null);
  const [spacePan, setSpacePan] = useState(false);
  const [viewportPanning, setViewportPanning] = useState(false);
  const scale = zoomMode === 'fit' ? fitScale : zoomMode;
  const dragRef = useRef(createDragController());
  const spacePanRef = useRef(false);
  const viewportPanRef = useRef<{ lastX: number; lastY: number } | null>(null);
  const viewportPannedRef = useRef(false);
  const objectGestureRef = useRef(false);
  const pendingAdditiveRef = useRef<{ objectId: string } | null>(null);
  const [pointerMods, setPointerMods] = useState({ shift: false, ctrlOrMeta: false, alt: false });
  const selectedObjectIds = selectedObjectIdsProp?.length ? selectedObjectIdsProp : (selectedObjectId ? [selectedObjectId] : []);
  const [marqueeRect, setMarqueeRect] = useState<MarqueeRect | null>(null);
  const marqueeRef = useRef<{
    startClientX: number;
    startClientY: number;
    startX: number;
    startY: number;
    additive: boolean;
    active: boolean;
    baseIds: string[];
  } | null>(null);
  const [objectMenu, setObjectMenu] = useState<{ x: number; y: number; objectId: string } | null>(null);
  const onGestureCommitRef = useRef(onGestureCommit);
  const onChangeRef = useRef(onChange);
  const onSelectRef = useRef(onSelect);
  const selectedObjectIdRef = useRef(selectedObjectId);
  const selectedObjectIdsRef = useRef(selectedObjectIds);
  const objectsRef = useRef(objects);
  const scaleRef = useRef(scale);
  const snapRef = useRef(snap);
  const smartGuidesRef = useRef(smartGuides);
  const readOnlyRef = useRef(readOnly);
  const onHistoryBeginRef = useRef(onHistoryBegin);
  const pageSizeRef = useRef(size);
  const justSelectedRef = useRef<string | null>(null);
  const pendingTableCellRef = useRef<{
    object: SlideObject;
    cellId: string;
    startX: number;
    startY: number;
    pointerId: number;
  } | null>(null);
  onGestureCommitRef.current = onGestureCommit;
  onChangeRef.current = onChange;
  onSelectRef.current = onSelect;
  snapRef.current = snap;
  smartGuidesRef.current = smartGuides;
  readOnlyRef.current = readOnly;
  onHistoryBeginRef.current = onHistoryBegin;
  pageSizeRef.current = size;
  selectedObjectIdRef.current = selectedObjectId;
  selectedObjectIdsRef.current = selectedObjectIds;
  objectsRef.current = objects;
  scaleRef.current = scale;

  React.useLayoutEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    const update = () => {
      const next = Math.max(0.18, Math.min((frame.clientWidth - 24) / size.width, (frame.clientHeight - 24) / size.height));
      setFitScale((current) => (Math.abs(current - next) < 0.002 ? current : next));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(frame);
    return () => observer.disconnect();
  }, [size.width, size.height]);

  const renderObjects = dragPreview?.objects ?? objects;
  const renderSelectedId = dragPreview?.selectedId ?? selectedObjectId;
  const renderSelectedIds = dragPreview?.selectedIds?.length ? dragPreview.selectedIds : selectedObjectIds;
  const selected = renderObjects.find((object) => object.id === renderSelectedId) || null;

  const setEditingTextId = (id: string | null) => onEditingTextChange?.(id);
  const [textFocusPoint, setTextFocusPoint] = React.useState<{ x: number; y: number } | null>(null);
  const [editingCellId, setEditingCellId] = React.useState<string | null>(null);

  const beginTextEdit = (object: SlideObject, point?: { clientX: number; clientY: number } | null) => {
    if (readOnly || object.locks?.content || !hasEditableText(object)) return false;
    onSelect(object.id, object.groupId ? { isolate: true } : undefined);
    setTextFocusPoint(point ? { x: point.clientX, y: point.clientY } : null);
    onPrepareTextEdit?.(object.id);
    setEditingTextId(object.id);
    return true;
  };

  React.useEffect(() => {
    if (editingTextId && !shouldKeepTextEdit(editingTextId, selectedObjectId, selectedObjectIds)) {
      setEditingTextId(null);
    }
    if (editingCellId && selectedObjectId) {
      const selectedTable = objects.find((object) => object.id === selectedObjectId);
      if (selectedTable?.type !== 'table') setEditingCellId(null);
    }
    if (!selectedObjectId) setEditingCellId(null);
    if (croppingId && croppingId !== selectedObjectId && !selectedObjectIds.includes(croppingId)) {
      onCroppingChange?.(null);
    }
    // Only re-check when the selection changes. Including editingTextId here
    // cleared the editor on the same click that opened it.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedObjectId, selectedObjectIds, croppingId, onCroppingChange, onEditingTextChange]);

  const changeZoom = (direction: 1 | -1) => {
    setZoomMode((current) => steppedZoom(current === 'fit' ? fitScale : current, direction));
  };

  React.useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    const onWheel = (event: WheelEvent) => {
      if (!(event.ctrlKey || event.metaKey)) return;
      event.preventDefault();
      changeZoom(event.deltaY < 0 ? 1 : -1);
    };
    frame.addEventListener('wheel', onWheel, { passive: false });
    return () => frame.removeEventListener('wheel', onWheel);
  }, [fitScale]);

  const endViewportPan = () => {
    viewportPanRef.current = null;
    setViewportPanning(false);
  };

  const beginViewportPan = (event: React.PointerEvent) => {
    event.preventDefault();
    event.stopPropagation();
    viewportPanRef.current = { lastX: event.clientX, lastY: event.clientY };
    viewportPannedRef.current = true;
    setViewportPanning(true);
  };

  const slidePoint = (clientX: number, clientY: number) => {
    const surface = frameRef.current?.querySelector('[data-slide-surface]') as HTMLElement | null;
    const rect = surface?.getBoundingClientRect();
    if (!rect) return null;
    return slidePointFromClient(clientX, clientY, rect, scaleRef.current);
  };

  const applyMarqueeSelection = (rect: MarqueeRect, additive: boolean, baseIds: string[]) => {
    const hits = objectsIntersectingRect(objectsRef.current, rect).map((object) => object.id);
    const next = nextMarqueeSelection(objectsRef.current, baseIds, hits, { additive });
    onSelectRef.current(next[next.length - 1] || null, { ids: next });
  };

  const beginMarquee = (event: React.PointerEvent) => {
    if (spacePanRef.current || dragRef.current.isActive() || event.button !== 0) return;
    if ((event.target as HTMLElement | null)?.closest?.('[data-slide-object], .ProseMirror, [data-presentation-toolbar]')) return;
    const point = slidePoint(event.clientX, event.clientY);
    if (!point) return;
    event.preventDefault();
    marqueeRef.current = {
      startClientX: event.clientX,
      startClientY: event.clientY,
      startX: point.x,
      startY: point.y,
      additive: event.shiftKey || event.ctrlKey || event.metaKey,
      active: false,
      baseIds: [...selectedObjectIdsRef.current],
    };
  };

  const moveMarquee = (event: PointerEvent) => {
    const session = marqueeRef.current;
    if (!session) return;
    const point = slidePoint(event.clientX, event.clientY);
    if (!point) return;
    if (!session.active && !isMarqueeGesture(event.clientX - session.startClientX, event.clientY - session.startClientY)) {
      return;
    }
    session.active = true;
    objectGestureRef.current = true;
    const rect = normalizeRect(session.startX, session.startY, point.x, point.y);
    setMarqueeRect(rect);
    applyMarqueeSelection(rect, session.additive, session.baseIds);
  };

  const endMarquee = (event?: PointerEvent) => {
    const session = marqueeRef.current;
    if (!session) return;
    marqueeRef.current = null;
    setMarqueeRect(null);
    if (!session.active) return;
    objectGestureRef.current = true;
    if (!event) return;
    const point = slidePoint(event.clientX, event.clientY);
    if (!point) return;
    applyMarqueeSelection(
      normalizeRect(session.startX, session.startY, point.x, point.y),
      session.additive,
      session.baseIds,
    );
  };

  const moveMarqueeRef = useRef(moveMarquee);
  const endMarqueeRef = useRef(endMarquee);
  moveMarqueeRef.current = moveMarquee;
  endMarqueeRef.current = endMarquee;

  React.useEffect(() => {
    const syncPointerMods = (event: Pick<KeyboardEvent, 'shiftKey' | 'ctrlKey' | 'metaKey' | 'altKey'>) => {
      setPointerMods((current) => {
        const next = {
          shift: event.shiftKey,
          ctrlOrMeta: event.ctrlKey || event.metaKey,
          alt: event.altKey,
        };
        if (current.shift === next.shift && current.ctrlOrMeta === next.ctrlOrMeta && current.alt === next.alt) {
          return current;
        }
        return next;
      });
    };
    const onKey = (event: KeyboardEvent) => {
      syncPointerMods(event);
      if (event.key === 'Escape' && croppingId) {
        event.preventDefault();
        onCroppingChange?.(null);
        return;
      }
      if (isSpacePanKey(event)) {
        if (!canStartSpacePan(event.target, event) || dragRef.current.isActive()) return;
        event.preventDefault();
        if (event.repeat || spacePanRef.current) return;
        spacePanRef.current = true;
        setSpacePan(true);
        return;
      }
      const zoom = isZoomKey(event);
      if (!zoom) return;
      if (event.target instanceof HTMLElement) {
        const tag = event.target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      }
      event.preventDefault();
      if (zoom === 'fit') setZoomMode('fit');
      else changeZoom(zoom === 'in' ? 1 : -1);
    };
    const onKeyUp = (event: KeyboardEvent) => {
      syncPointerMods(event);
      if (!isSpacePanKey(event)) return;
      spacePanRef.current = false;
      setSpacePan(false);
      endViewportPan();
    };
    const onWindowBlur = () => {
      spacePanRef.current = false;
      setSpacePan(false);
      setPointerMods({ shift: false, ctrlOrMeta: false, alt: false });
      endViewportPan();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onWindowBlur);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onWindowBlur);
    };
  }, [croppingId, onCroppingChange, fitScale]);

  React.useEffect(() => {
    const onMove = (event: PointerEvent) => {
      if (marqueeRef.current) {
        moveMarqueeRef.current(event);
        return;
      }
      const pan = viewportPanRef.current;
      const scroller = scrollRef.current;
      if (!pan || !scroller) return;
      const dx = event.clientX - pan.lastX;
      const dy = event.clientY - pan.lastY;
      applyViewportPan(scroller, dx, dy);
      pan.lastX = event.clientX;
      pan.lastY = event.clientY;
    };
    const onUp = (event: PointerEvent) => {
      if (marqueeRef.current) endMarqueeRef.current(event);
      if (viewportPanRef.current) endViewportPan();
    };
    const onBlur = () => {
      endMarqueeRef.current();
      if (viewportPanRef.current) endViewportPan();
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  const commitObjects = (next: SlideObject[]) => {
    onChange(next);
  };

  const updateObject = (id: string, patch: Partial<SlideObject>) => {
    commitObjects(objects.map((object) => (object.id === id ? { ...object, ...patch } : object)));
  };

  const endDrag = () => {
    const commit = dragRef.current.finish();
    setDragPreview(null);
    if (commit) {
      pendingAdditiveRef.current = null;
      objectGestureRef.current = true;
      (onGestureCommitRef.current || onChangeRef.current)(commit.objects);
      if (commit.selectedIds?.length) {
        onSelectRef.current(commit.selectedId, { ids: commit.selectedIds });
      } else if (commit.selectedId !== selectedObjectIdRef.current) {
        onSelectRef.current(commit.selectedId);
      }
      return;
    }
    const pending = shouldApplyPendingAdditiveToggle(pendingAdditiveRef.current, false);
    pendingAdditiveRef.current = null;
    if (pending) {
      objectGestureRef.current = true;
      onSelectRef.current(pending.objectId, { additive: true });
    }
  };
  const endDragRef = useRef(endDrag);
  endDragRef.current = endDrag;

  const startDeferredTableMove = (pending: {
    object: SlideObject;
    startX: number;
    startY: number;
    pointerId: number;
  }, pointer: { clientX: number; clientY: number; shiftKey?: boolean; altKey?: boolean }) => {
    const liveObjects = objectsRef.current;
    const object = liveObjects.find((item) => item.id === pending.object.id) || pending.object;
    const nextIds = selectedObjectIdsRef.current.includes(object.id)
      ? selectedObjectIdsRef.current
      : [object.id];
    if (!canStartDrag(object, 'move', readOnlyRef.current)) return;
    if (!canMoveSelection(liveObjects, nextIds)) return;
    const surface = frameRef.current?.querySelector('[data-slide-surface]') as HTMLElement | null;
    const rect = surface?.getBoundingClientRect();
    const page = pageSizeRef.current;
    dragRef.current.start({
      object,
      objects: liveObjects,
      pointer: { clientX: pending.startX, clientY: pending.startY },
      mode: 'move',
      startAngle: rotationFromPointer(
        rect ? rect.left + (object.x + object.w / 2) * scaleRef.current : pointer.clientX,
        rect ? rect.top + (object.y + object.h / 2) * scaleRef.current : pointer.clientY,
        pending.startX,
        pending.startY,
      ),
      companionIds: nextIds.filter((id) => id !== object.id),
    }, onHistoryBeginRef.current);
    objectGestureRef.current = true;
    try {
      surface?.setPointerCapture(pending.pointerId);
    } catch {
      /* capture is optional once the window listener has started the move */
    }
    dragRef.current.move(
      pointer,
      {
        scale: scaleRef.current,
        snap: Boolean(snapRef.current),
        smartGuides: Boolean(smartGuidesRef.current),
        pageSize: { width: page.width, height: page.height },
      },
      setDragPreview,
    );
  };

  React.useEffect(() => {
    const onMove = (event: PointerEvent) => {
      const pending = pendingTableCellRef.current;
      if (!pending) return;
      if (Math.hypot(event.clientX - pending.startX, event.clientY - pending.startY) <= MOVE_THRESHOLD_PX) return;
      pendingTableCellRef.current = null;
      setEditingCellId(null);
      startDeferredTableMove(pending, {
        clientX: event.clientX,
        clientY: event.clientY,
        shiftKey: event.shiftKey,
        altKey: event.altKey,
      });
    };
    const onUp = () => {
      const pending = pendingTableCellRef.current;
      if (!pending) return;
      pendingTableCellRef.current = null;
      if (!pending.object.locks.content) setEditingCellId(pending.cellId);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, []);

  React.useEffect(() => {
    const drag = dragRef.current;
    const onWindowEnd = () => {
      if (drag.isActive()) endDragRef.current();
    };
    window.addEventListener('pointerup', onWindowEnd);
    window.addEventListener('pointercancel', onWindowEnd);
    window.addEventListener('blur', onWindowEnd);
    return () => {
      window.removeEventListener('pointerup', onWindowEnd);
      window.removeEventListener('pointercancel', onWindowEnd);
      window.removeEventListener('blur', onWindowEnd);
      const commit = drag.finish();
      if (commit) (onGestureCommitRef.current || onChangeRef.current)(commit.objects);
    };
  }, []);

  const duplicateSelected = () => {
    const selectedObjects = objects.filter((object) => selectedObjectIds.includes(object.id) && !object.locks.delete);
    if (!selectedObjects.length) return;
    const copies = cloneSelection(selectedObjects, objects);
    onChange([...objects, ...copies]);
    const primary = copies[copies.length - 1];
    if (primary) onSelect(primary.id);
  };

  const deleteSelected = () => {
    const removable = new Set(objects.filter((object) => selectedObjectIds.includes(object.id) && !object.locks.delete).map((object) => object.id));
    if (!removable.size) return;
    onChange(objects.filter((object) => !removable.has(object.id)));
    onSelect(null);
  };

  const onPointerDown = (
    event: React.PointerEvent,
    object: SlideObject,
    mode: 'move' | 'resize' | 'crop' | 'rotate' | 'pan' | 'scaleImage',
    handle?: ResizeHandle
  ) => {
    if (spacePanRef.current) {
      beginViewportPan(event);
      return;
    }
    event.stopPropagation();
    const additive = event.shiftKey || event.ctrlKey || event.metaKey;
    const isolate = !additive && (Boolean(object.groupId && isolatedObjectId === object.id)
      || selectionIsolateMode(objects, isolatedObjectId, object.id, additive));
    const alreadySelected = selectedObjectIds.includes(object.id);
    const deferToggle = shouldDeferAdditiveToggle(additive, object.id, selectedObjectIds);
    const keepSelection = (mode === 'move' || mode === 'resize')
      && !isolate
      && alreadySelected
      && (selectedObjectIds.length > 1 || deferToggle);
    const nextIds = keepSelection
      ? selectedObjectIds
      : gestureTargetIds(objects, selectedObjectIds, object.id, { additive, isolate });
    if (!keepSelection) onSelect(object.id, { additive, isolate });
    pendingAdditiveRef.current = deferToggle ? { objectId: object.id } : null;
    if (mode !== 'move') objectGestureRef.current = true;
    if (mode === 'resize' && nextIds.length > 1) {
      if (readOnly || nextIds.every((id) => objects.find((item) => item.id === id)?.locks.size)) return;
    } else if (!canStartDrag(object, mode, readOnly)) return;
    if (mode === 'move' && !canMoveSelection(objects, nextIds)) return;
    const surface = frameRef.current?.querySelector('[data-slide-surface]') as HTMLElement | null;
    const rect = surface?.getBoundingClientRect();
    const centerX = rect ? rect.left + (object.x + object.w / 2) * scale : event.clientX;
    const centerY = rect ? rect.top + (object.y + object.h / 2) * scale : event.clientY;
    dragRef.current.start({
      object,
      objects,
      pointer: { clientX: event.clientX, clientY: event.clientY },
      mode,
      handle,
      startAngle: rotationFromPointer(centerX, centerY, event.clientX, event.clientY),
      companionIds: mode === 'move' || mode === 'resize' ? nextIds.filter((id) => id !== object.id) : [],
    }, onHistoryBegin);
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: React.PointerEvent) => {
    if (!dragRef.current.isActive()) return;
    const session = dragRef.current.getSession();
    const surface = frameRef.current?.querySelector('[data-slide-surface]') as HTMLElement | null;
    const rect = surface?.getBoundingClientRect();
    const rotationCenter = session && rect
      ? {
        x: rect.left + (session.origX + session.origW / 2) * scale,
        y: rect.top + (session.origY + session.origH / 2) * scale,
      }
      : undefined;
    dragRef.current.move(
      { clientX: event.clientX, clientY: event.clientY, shiftKey: event.shiftKey, altKey: event.altKey },
      { scale, snap, smartGuides, pageSize: { width: size.width, height: size.height }, rotationCenter },
      setDragPreview,
    );
  };

  const sorted = useMemo(() => [...renderObjects].sort((a, b) => (a.z || 0) - (b.z || 0)), [renderObjects]);
  const groupResizeTarget = selected && selectedObjectIds.length > 1
    ? { ...selected, ...(selectionBounds(renderObjects, selectedObjectIds) || selected), style: { ...selected.style, rotation: 0 } }
    : selected;
  const canResizeSelection = selectedObjectIds.length > 1
    ? selectedObjectIds.some((id) => !renderObjects.find((item) => item.id === id)?.locks.size)
    : Boolean(selected && !selected.locks.size);

  return (
    <Box
      ref={frameRef}
      onPointerDown={(event) => {
        if (spacePanRef.current) {
          beginViewportPan(event);
          return;
        }
        beginMarquee(event);
      }}
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        bgcolor: '#E8E4DA',
        position: 'relative',
        cursor: spacePan ? (viewportPanning ? 'grabbing' : 'grab') : (marqueeRect ? 'crosshair' : undefined),
        userSelect: spacePan || marqueeRect ? 'none' : undefined,
        ...(spacePan ? { '& *': { cursor: `${viewportPanning ? 'grabbing' : 'grab'} !important` } } : {}),
      }}
      onClick={() => {
        if (consumeFlag(dropConsumedRef) || consumeFlag(viewportPannedRef) || consumeFlag(objectGestureRef)) return;
        if (croppingId) onCroppingChange?.(null);
        onSelect(null);
      }}
      onDragOver={(event) => {
        if (readOnly) return;
        if (editingTextId && (event.target as HTMLElement | null)?.closest?.('.ProseMirror')) return;
        const types = Array.from(event.dataTransfer.types);
        if (onDropAsset && types.includes(PRESENTATION_ASSET_DRAG)) {
          event.preventDefault();
          event.dataTransfer.dropEffect = 'copy';
          return;
        }
        if (onDropFiles && types.includes('Files')) {
          event.preventDefault();
          event.dataTransfer.dropEffect = 'copy';
          return;
        }
        if (onDropText && !types.includes('Files') && hasLibraryFieldTransfer(types)) {
          event.preventDefault();
          event.dataTransfer.dropEffect = 'copy';
        }
      }}
      onDrop={(event) => {
        if (readOnly) return;
        if (editingTextId && (event.target as HTMLElement | null)?.closest?.('.ProseMirror')) return;
        const types = Array.from(event.dataTransfer.types);
        const frame = frameRef.current?.querySelector('[data-slide-surface]') as HTMLElement | null;
        const rect = (frame || frameRef.current)?.getBoundingClientRect();
        if (onDropAsset && types.includes(PRESENTATION_ASSET_DRAG)) {
          const raw = event.dataTransfer.getData(PRESENTATION_ASSET_DRAG);
          if (!raw) return;
          event.preventDefault();
          dropConsumedRef.current = true;
          try {
            const asset = JSON.parse(raw) as VisibleAsset;
            if (!rect) return;
            const x = (event.clientX - rect.left) / scale - 320;
            const y = (event.clientY - rect.top) / scale - 240;
            onDropAsset(asset, Math.max(40, x), Math.max(40, y));
          } catch {
            return;
          }
          return;
        }
        if (onDropFiles && types.includes('Files')) {
          event.preventDefault();
          dropConsumedRef.current = true;
          if (!rect) return;
          const x = (event.clientX - rect.left) / scale - 320;
          const y = (event.clientY - rect.top) / scale - 240;
          onDropFiles(readClipboardImageFile(event.dataTransfer), Math.max(40, x), Math.max(40, y));
          return;
        }
        if (!onDropText || types.includes('Files')) return;
        const payload = readLibraryFieldTransfer(event.dataTransfer);
        if (!payload || !rect) return;
        event.preventDefault();
        dropConsumedRef.current = true;
        const x = (event.clientX - rect.left) / scale;
        const y = (event.clientY - rect.top) / scale;
        onDropText(payload, x, y);
      }}
    >
      <Box
        ref={scrollRef}
        sx={{
          position: 'absolute',
          inset: 0,
          overflow: 'auto',
        }}
      >
      <Box
        sx={{
          minWidth: '100%',
          minHeight: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxSizing: 'border-box',
          p: 1.5,
        }}
      >
      <Box
        data-slide-surface="true"
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onLostPointerCapture={endDrag}
        sx={{
          width: size.width * scale,
          height: size.height * scale,
          position: 'relative',
          boxShadow: '0 18px 50px rgba(10,39,87,0.18)',
          bgcolor: backgroundColor,
          backgroundImage: showGrid
            ? 'linear-gradient(to right, rgba(27,57,103,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(27,57,103,0.08) 1px, transparent 1px)'
            : 'none',
          backgroundSize: showGrid ? `${8 * scale}px ${8 * scale}px` : undefined,
        }}
      >
        <Box sx={{ width: size.width, height: size.height, transform: `scale(${scale})`, transformOrigin: 'top left', position: 'absolute' }}>
          {sorted.map((object) => {
            const isSelected = object.id === renderSelectedId;
            const isInSelection = renderSelectedIds.includes(object.id) || isSelected;
            const isMedia = object.type === 'image' || object.type === 'product';
            const isShape = object.type === 'shape';
            const isCropping = Boolean(isCroppableObject(object) && croppingId === object.id);
            const isolatedGroupId = isolatedObjectId
              ? objects.find((item) => item.id === isolatedObjectId)?.groupId
              : null;
            const isIsolateSibling = Boolean(isolatedGroupId && object.groupId === isolatedGroupId && object.id !== isolatedObjectId);
            return (
              <Box
                key={object.id}
                data-slide-object="true"
                onClick={(event) => {
                  event.stopPropagation();
                  consumeFlag(dropConsumedRef);
                  consumeFlag(viewportPannedRef);
                  const dragged = consumeFlag(objectGestureRef);
                  const justSelected = justSelectedRef.current === object.id;
                  if (justSelected) justSelectedRef.current = null;
                  if (
                    object.type === 'table'
                    && isSelected
                    && selectedObjectIds.length < 2
                    && !readOnly
                    && !object.locks.content
                    && !dragged
                    && !justSelected
                    && !pendingTableCellRef.current
                    && !(event.shiftKey || event.ctrlKey || event.metaKey)
                  ) {
                    const divider = (event.target as HTMLElement).closest('[data-table-divider]');
                    if (divider) return;
                    const cellId = (event.target as HTMLElement).closest('[data-table-cell]')?.getAttribute('data-table-cell');
                    if (cellId) setEditingCellId(cellId);
                  }
                }}
                onContextMenu={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (!selectedObjectIds.includes(object.id)) {
                    onSelect(object.id);
                  }
                  setObjectMenu({ x: event.clientX, y: event.clientY, objectId: object.id });
                }}
                onDoubleClick={(event) => {
                  if (isCroppableObject(object)) {
                    onSelect(object.id, { isolate: true });
                    onCroppingChange?.(object.id);
                    return;
                  }
                  if (object.type === 'table') {
                    onSelect(object.id);
                    const cellId = (event.target as HTMLElement).closest('[data-table-cell]')?.getAttribute('data-table-cell');
                    if (cellId && !readOnly && !object.locks.content) setEditingCellId(cellId);
                    return;
                  }
                  beginTextEdit(object, event);
                }}
                onPointerDown={(event) => {
                  if (spacePanRef.current) {
                    beginViewportPan(event);
                    return;
                  }
                  if (hasEditableText(object) && editingTextId === object.id) {
                    event.stopPropagation();
                    return;
                  }
                  if (object.type === 'table' && editingCellId) {
                    const moveWithModifier = event.shiftKey || event.ctrlKey || event.metaKey;
                    if (!moveWithModifier || event.button !== 0) {
                      const target = event.target as HTMLElement;
                      if (target.closest('[data-table-cell-editor]')) {
                        event.stopPropagation();
                        return;
                      }
                      if (target.closest('[data-table-divider]')) {
                        setEditingCellId(null);
                        event.stopPropagation();
                        return;
                      }
                      const nextCellId = target.closest('[data-table-cell]')?.getAttribute('data-table-cell');
                      if (nextCellId && !object.locks.content) {
                        setEditingCellId(nextCellId);
                        event.stopPropagation();
                        return;
                      }
                      setEditingCellId(null);
                      event.stopPropagation();
                      return;
                    }
                    setEditingCellId(null);
                  }
                  if (event.button !== 0) {
                    event.stopPropagation();
                    return;
                  }
                  const additive = event.shiftKey || event.ctrlKey || event.metaKey;
                  if (additive && editingTextId) setEditingTextId(null);
                  if (
                    shouldEnterTextEditOnSelect(object, objects, {
                      additive,
                      isolatedObjectId,
                      selectedIds: selectedObjectIds,
                    })
                    && beginTextEdit(object, event)
                  ) {
                    event.stopPropagation();
                    return;
                  }
                  if (event.detail >= 2 && isCroppableObject(object)) {
                    event.stopPropagation();
                    onSelect(object.id, { isolate: true });
                    onCroppingChange?.(object.id);
                    return;
                  }
                  if (event.detail >= 2 && beginTextEdit(object, event)) {
                    event.stopPropagation();
                    return;
                  }
                  if (object.type === 'table') {
                    const target = event.target as HTMLElement;
                    if (target.closest('[data-table-divider]')) {
                      event.stopPropagation();
                      return;
                    }
                    const cellId = target.closest('[data-table-cell]')?.getAttribute('data-table-cell');
                    if (
                      cellId
                      && isSelected
                      && selectedObjectIds.length < 2
                      && !readOnly
                      && event.detail < 2
                      && !additive
                    ) {
                      pendingTableCellRef.current = {
                        object,
                        cellId,
                        startX: event.clientX,
                        startY: event.clientY,
                        pointerId: event.pointerId,
                      };
                      event.stopPropagation();
                      return;
                    }
                    if (!selectedObjectIds.includes(object.id)) {
                      justSelectedRef.current = object.id;
                    }
                  }
                  onPointerDown(event, object, isCropping ? 'pan' : 'move');
                }}
                sx={{
                  position: 'absolute',
                  left: object.x,
                  top: object.y,
                  width: object.w,
                  height: object.h,
                  zIndex: isCropping ? 11000 : isInSelection ? 10000 : object.z,
                  outline: isIsolateSibling
                    ? `${1.5 / scale}px dashed ${PRES.gold}`
                    : isInSelection && !isSelected ? `${1.5 / scale}px solid ${PRES.gold}` : undefined,
                  outlineOffset: isInSelection && !isSelected ? 2 : undefined,
                  overflow: object.type === 'text' || object.type === 'table' || isSelected || isShape || isCropping ? 'visible' : 'hidden',
                  cursor: objectHoverCursor(object, {
                    editing: editingTextId === object.id
                      || (
                        object.type === 'table'
                        && Boolean(editingCellId)
                        && !pointerMods.shift
                        && !pointerMods.ctrlOrMeta
                      ),
                    cropping: isCropping,
                    selectedCount: readOnly ? 1 : selectedObjectIds.length,
                    isSelected: isInSelection,
                    moveModifier: !readOnly && (pointerMods.shift || pointerMods.ctrlOrMeta),
                    copyModifier: !readOnly && pointerMods.alt,
                  }),
                  userSelect: editingTextId === object.id || (object.type === 'table' && editingCellId) ? 'text' : 'none',
                  background: isMedia && !isShape ? (object.style?.backgroundColor || 'transparent') : 'transparent',
                  ...objectSurfaceSx(object),
                  ...objectEffectSx(object),
                  color: object.style?.color || '#111111',
                  fontFamily: resolveSlideFontFamily(object.style?.fontFamily),
                  fontSize: object.style?.fontSize || 24,
                  fontWeight: resolvedFontWeight(object.style),
                  fontStyle: object.style?.fontStyle === 'italic' ? 'italic' : undefined,
                  textDecoration: object.style?.textDecoration === 'underline' ? 'underline' : undefined,
                  textAlign: object.style?.textAlign || 'left',
                  overflowWrap: object.type === 'text' || isFillableShape(object) ? 'break-word' : undefined,
                  lineHeight: resolvedLineHeight(object.style),
                  letterSpacing: object.style?.letterSpacing != null ? `${object.style.letterSpacing}px` : undefined,
                  textTransform: object.style?.textTransform || 'none',
                  display: object.type === 'text' ? 'flex' : undefined,
                  flexDirection: object.type === 'text' ? 'column' : undefined,
                  justifyContent: object.style?.verticalAlign === 'middle' ? 'center' : object.style?.verticalAlign === 'bottom' ? 'flex-end' : 'flex-start',
                  opacity: object.style?.opacity ?? 1,
                  transform: object.style?.rotation ? `rotate(${object.style.rotation}deg)` : undefined,
                  transformOrigin: 'center center',
                  ...(object.type === 'text' ? textFlowSx : {}),
                }}
              >
                {object.type === 'text' && editingTextId === object.id ? (
                  <TextObjectEditor
                    object={object}
                    locked={Boolean(object.locks?.content)}
                    focusPoint={textFocusPoint}
                    onReady={onTextEditorReady}
                    onBeforeContentChange={(html) => onBeforeTextContentChange?.(object, html) ?? true}
                    onFit={object.type === 'text' ? (html) => onTextBoxFit?.(object.id, html) : undefined}
                    onCommit={(html) => {
                      const next = stripHtml(html) === '' ? '' : html;
                      const fitted = object.type === 'text' ? applyTextBoxFit({ ...object, content: next }, next, size) : object;
                      if (next !== (object.content || '') || fitted.w !== object.w || fitted.h !== object.h) {
                        updateObject(object.id, { content: next, w: fitted.w, h: fitted.h });
                      }
                    }}
                  />
                ) : object.type === 'text' ? (
                  <Box sx={{ width: '100%' }} dangerouslySetInnerHTML={{ __html: objectPreviewHtml(object) || 'Text' }} />
                ) : object.type === 'table' ? (
                  <TableObjectView
                    object={object}
                    interactive={!readOnly && !object.locks.content}
                    editingCellId={isSelected ? editingCellId : null}
                    canResizeLayout={isSelected && selectedObjectIds.length < 2 && !readOnly && !object.locks.size}
                    layoutHitPx={8 / Math.max(scale, 0.2)}
                    onLayoutBegin={onHistoryBegin}
                    onTableCommit={(table) => {
                      const next = objects.map((item) => (item.id === object.id ? { ...item, table } : item));
                      (onGestureCommit || onChange)(next);
                    }}
                    onEditCell={(cellId) => {
                      if (!isSelected) return;
                      setEditingCellId(cellId);
                    }}
                    onCommitCell={(cellId, html) => {
                      const table = normalizeTable(object.table);
                      updateObject(object.id, {
                        table: {
                          ...table,
                          rows: table.rows.map((row) => ({
                            cells: row.cells.map((cell) => (
                              cell.id === cellId ? { ...cell, content: sanitizePresentationHtml(html) } : cell
                            )),
                          })),
                        },
                      });
                    }}
                  />
                ) : object.type === 'chart' ? (
                  <ChartObjectView object={object} />
                ) : isShape ? (
                  <ShapeObjectView object={object}>
                    {editingTextId === object.id && !isLineShape(object.style) ? (
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: object.style?.verticalAlign === 'middle' ? 'center' : object.style?.verticalAlign === 'bottom' ? 'flex-end' : 'flex-start',
                          boxSizing: 'border-box',
                          p: `${object.style?.padding || 0}px`,
                          ...textFlowSx,
                        }}
                      >
                        <TextObjectEditor
                          object={object}
                          locked={Boolean(object.locks?.content)}
                          focusPoint={textFocusPoint}
                          onReady={onTextEditorReady}
                          onBeforeContentChange={(html) => onBeforeTextContentChange?.(object, html) ?? true}
                          onCommit={(html) => {
                            const next = stripHtml(html) === '' ? '' : html;
                            if (next !== (object.content || '')) {
                              updateObject(object.id, { content: next });
                            }
                          }}
                        />
                      </Box>
                    ) : object.content && !isLineShape(object.style) ? (
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: object.style?.verticalAlign === 'middle' ? 'center' : object.style?.verticalAlign === 'bottom' ? 'flex-end' : 'flex-start',
                          boxSizing: 'border-box',
                          p: `${object.style?.padding || 0}px`,
                          pointerEvents: 'none',
                          ...textFlowSx,
                        }}
                        dangerouslySetInnerHTML={{ __html: objectPreviewHtml(object) }}
                      />
                    ) : null}
                  </ShapeObjectView>
                ) : isMedia && object.imageUrl ? (
                  <Box sx={{ width: '100%', height: '100%', position: 'relative', overflow: isCropping ? 'visible' : 'hidden', ...mediaRadiusSx(object) }}>
                    {isCropping && (
                      <Box
                        component="img"
                        src={resolveMediaUrl(object.imageUrl)}
                        alt=""
                        sx={{
                          display: 'block',
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          pointerEvents: 'none',
                          opacity: 0.38,
                          ...imageCropStyle(object.style),
                        }}
                      />
                    )}
                    <Box sx={{ width: '100%', height: '100%', overflow: 'hidden', position: 'relative', ...mediaRadiusSx(object) }}>
                      <Box
                        component="img"
                        src={resolveMediaUrl(object.imageUrl)}
                        alt=""
                        sx={{ display: 'block', pointerEvents: 'none', ...imageCropStyle(object.style) }}
                      />
                    </Box>
                    {isCropping && (
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          outline: `${1.5 / scale}px solid ${PRES.gold}`,
                          pointerEvents: 'none',
                          zIndex: 2,
                        }}
                      />
                    )}
                  </Box>
                ) : isMedia ? (
                  <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1B3967', fontFamily: '"Akkurat Std", Arial, sans-serif', fontSize: 22 }}>
                    {object.type === 'product' ? 'Välj produkt från Athena' : 'Välj bild från Athena'}
                  </Box>
                ) : null}
                {object.conversionState && object.conversionState !== 'native' && (
                  <Box sx={{
                    position: 'absolute', top: 4, left: 4, px: 0.6, py: 0.1, borderRadius: 0.5,
                    bgcolor: 'rgba(27,57,103,0.88)', color: '#fff', fontSize: 10, lineHeight: 1.4, pointerEvents: 'none',
                  }}>
                    {object.conversionState === 'raster' ? 'Raster' : 'Delvis'}
                  </Box>
                )}
                {aiBusy && isSelected && hasEditableText(object) && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'rgba(255,255,255,0.58)',
                      pointerEvents: 'none',
                      zIndex: 3,
                    }}
                  >
                    <Box sx={{
                      px: 1,
                      py: 0.35,
                      borderRadius: 0.5,
                      bgcolor: PRES.navy,
                      color: '#fff',
                      fontSize: Math.max(11, 13 / scale),
                      fontWeight: 650,
                      letterSpacing: 0.2,
                    }}>
                      {aiBusyLabel || COPY.aiRewriteBusy}
                    </Box>
                  </Box>
                )}
                {isSelected && (
                  <SelectionChrome
                    object={object}
                    scale={scale}
                    hideSideHandles={isCroppableObject(object)}
                    hideResizeHandles
                    showTextFormat={hasEditableText(object) && editingTextId === object.id}
                    onTextStyle={onTextStyle}
                    onToggleMark={onToggleMark}
                    queryTextMark={queryTextMark}
                    showAi={Boolean(showAi && !readOnly && hasEditableText(object) && !object.locks?.content)}
                    aiBusy={aiBusy}
                    onAiRewrite={onAiRewrite}
                    onAiGrammar={onAiGrammar}
                    onAiTranslate={onAiTranslate}
                    onHandle={(event, handle, crop) => onPointerDown(event, object, crop ? 'crop' : 'resize', handle)}
                    onRotate={(event) => onPointerDown(event, object, 'rotate')}
                    onMove={hasEditableText(object) && !object.locks.position
                      ? (event) => onPointerDown(event, object, 'move')
                      : undefined}
                    onEditText={!readOnly && isFillableShape(object) && editingTextId !== object.id && !object.locks?.content
                      ? () => {
                          onCroppingChange?.(null);
                          beginTextEdit(object);
                        }
                      : undefined}
                    onFitImage={!readOnly && isCroppableObject(object)
                      ? () => updateObject(object.id, { style: fitImageInFrame(object).style })
                      : undefined}
                    onDuplicate={duplicateSelected}
                    onDelete={deleteSelected}
                    showLock={advancedMode && !readOnly}
                    onToggleLock={() => updateObject(object.id, {
                      locks: {
                        ...object.locks,
                        position: !object.locks.position,
                        size: !object.locks.size,
                        content: !object.locks.content,
                        style: !object.locks.style,
                      },
                    })}
                  />
                )}
              </Box>
            );
          })}
          <SmartGuidesOverlay guides={dragPreview?.guides} pageSize={size} scale={scale} />
          {marqueeRect && (
            <Box
              sx={{
                position: 'absolute',
                left: marqueeRect.x,
                top: marqueeRect.y,
                width: Math.max(1, marqueeRect.w),
                height: Math.max(1, marqueeRect.h),
                border: `${1.5 / scale}px solid ${PRES.gold}`,
                bgcolor: 'rgba(196,163,90,0.12)',
                pointerEvents: 'none',
                zIndex: 30000,
              }}
            />
          )}
          {selected && isCroppableObject(selected) && selectedObjectIds.length < 2 && !selected.locks.size && (
            <ScreenAlignedCropHandles
              object={selected}
              scale={scale}
              onHandle={(event, handle) => onPointerDown(event, selected, 'crop', handle)}
            />
          )}
          {selected && canResizeSelection && croppingId !== selected.id && groupResizeTarget && (
            <ScreenAlignedResizeHandles
              object={groupResizeTarget}
              scale={scale}
              cornersOnly={isCroppableObject(selected) || selectedObjectIds.length > 1}
              onHandle={(event, handle) => onPointerDown(event, selected, 'resize', handle)}
            />
          )}
          {selected && croppingId === selected.id && isCroppableObject(selected) && (
            <>
              <ImageScaleHandles
                object={selected}
                scale={scale}
                onHandle={(event, handle) => onPointerDown(event, selected, 'scaleImage', handle)}
              />
              <CropSessionBar
                object={selected}
                scale={scale}
                onReset={() => {
                  updateObject(selected.id, {
                    style: {
                      ...selected.style,
                      cropLeft: 0,
                      cropTop: 0,
                      cropRight: 0,
                      cropBottom: 0,
                    },
                  });
                }}
                onDone={() => onCroppingChange?.(null)}
              />
            </>
          )}
        </Box>
      </Box>
      </Box>
      </Box>
      <Menu
        open={Boolean(objectMenu)}
        onClose={() => setObjectMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={objectMenu ? { top: objectMenu.y, left: objectMenu.x } : undefined}
      >
        {!readOnly && (
          <MenuItem onClick={() => { onBringToFront?.(); setObjectMenu(null); }}>{COPY.bringToFront}</MenuItem>
        )}
        {!readOnly && (
          <MenuItem onClick={() => { onSendToBack?.(); setObjectMenu(null); }}>{COPY.sendToBack}</MenuItem>
        )}
        {!readOnly && (
          <MenuItem
            disabled={!canGroup(selectedObjectIds)}
            onClick={() => { onGroup?.(); setObjectMenu(null); }}
          >
            {COPY.groupObjects}
          </MenuItem>
        )}
        {!readOnly && (
          <MenuItem
            disabled={!canUngroup(objects, selectedObjectIds)}
            onClick={() => { onUngroup?.(); setObjectMenu(null); }}
          >
            {COPY.ungroupObjects}
          </MenuItem>
        )}
        {(() => {
          const menuObject = objects.find((object) => object.id === objectMenu?.objectId);
          const bound = resolveBoundField(objects, menuObject);
          if (!menuObject || readOnly || !onMakeBoundFieldLocal || bound?.source !== 'direct') return null;
          return (
            <MenuItem onClick={() => {
              onMakeBoundFieldLocal(menuObject.id);
              setObjectMenu(null);
            }}>
              {COPY.boundFieldMakeLocal}
            </MenuItem>
          );
        })()}
        {(() => {
          const menuObject = objects.find((object) => object.id === objectMenu?.objectId);
          if (!menuObject || !isCroppableObject(menuObject) || readOnly) return null;
          return (
            <MenuItem onClick={() => {
              updateObject(menuObject.id, { style: fitImageInFrame(menuObject).style });
              setObjectMenu(null);
            }}>
              {COPY.fitImageInFrame}
            </MenuItem>
          );
        })()}
        {(() => {
          const menuObject = objects.find((object) => object.id === objectMenu?.objectId);
          const assetId = menuObject?.assetId;
          if (!assetId || (menuObject?.type !== 'image' && menuObject?.type !== 'product')) return null;
          return (
            <MenuItem
              onClick={(event) => {
                event.stopPropagation();
                setObjectMenu(null);
                onShowInLibrary?.(assetId);
              }}
            >
              {COPY.showInLibrary}
            </MenuItem>
          );
        })()}
      </Menu>
      <Box
        onPointerDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
        sx={{
          position: 'absolute',
          right: 12,
          bottom: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
          bgcolor: PRES.white,
          border: `1px solid ${PRES.line}`,
          borderRadius: 5,
          px: 0.4,
          py: 0.15,
          boxShadow: '0 8px 20px rgba(10,39,87,0.12)',
          zIndex: 6,
        }}
      >
        <IconButton size="small" title={COPY.zoomOut} onClick={() => changeZoom(-1)} sx={{ color: PRES.textPrimary }}>
          <RemoveIcon fontSize="small" />
        </IconButton>
        <Box
          component="button"
          type="button"
          title={COPY.zoomFit}
          onClick={() => setZoomMode('fit')}
          style={{
            border: 0,
            background: 'none',
            minWidth: 44,
            cursor: 'pointer',
            color: PRES.textPrimary,
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {formatZoomPercent(scale)}
        </Box>
        <IconButton size="small" title={COPY.zoomIn} onClick={() => changeZoom(1)} sx={{ color: PRES.textPrimary }}>
          <AddIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" title={COPY.zoomFit} onClick={() => setZoomMode('fit')} sx={{ color: PRES.textPrimary }}>
          <FitScreenIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

export default SlideCanvas;
